import { inject, Injectable, signal } from '@angular/core';
import { toSignal, toObservable } from '@angular/core/rxjs-interop';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { GetDeviceTypesGQL, GetNavigationDataGQL } from '@graphql/graphql';

@Injectable({
  providedIn: 'root',
})
export class NavigationDataService {
  updatePortfoliosList = signal(false);
  portfolioListError = signal<unknown | null>(null);
  private portfolioListGQL = inject(GetNavigationDataGQL);
  private deviceTypesGQL = inject(GetDeviceTypesGQL);

  getPortfolioList() {
    return toSignal<any>(
      toObservable(this.updatePortfoliosList).pipe(
        tap(() => {
          this.portfolioListError.set(null);
        }),
        switchMap(() =>
          this.portfolioListGQL.fetch({}, { fetchPolicy: 'network-only' }).pipe(
            map(result => {
              return result.data.Portfolio.items.map(portfolio => {
                const sites = [];
                const devices = [];

                const groupedData = Object.values(
                  portfolio.Sites.items.reduce(
                    (acc, item) => {
                      const countryId = String(item._id_country).trim();
                      if (!acc[countryId]) {
                        acc[countryId] = {
                          _id: countryId,
                          name: item.country,
                          checked: true,
                          sites: [],
                          regions: [],
                        };
                      }

                      // Add to flat sites list
                      acc[countryId].sites.push(item);

                      // Group into regions if region data exists
                      if (item._id_region && item.regionName) {
                        const regionId = String(item._id_region).trim();
                        let region = acc[countryId].regions.find(r => r._id === regionId);
                        if (!region) {
                          region = {
                            _id: regionId,
                            name: item.regionName,
                            sites: [],
                          };
                          acc[countryId].regions.push(region);
                        }
                        region.sites.push(item);
                      }

                      return acc;
                    },
                    {} as Record<
                      string,
                      {
                        _id: string;
                        name: string;
                        checked: boolean;
                        sites: any[];
                        regions: { _id: string; name: string; sites: any[] }[];
                      }
                    >,
                  ),
                );

                const countries = portfolio.Sites.items.map(item => {
                  const siteDevices = item.Devices.items.map(device => ({
                    ...device,
                    checked: true,
                    country: item.country,
                    site: item._id,
                  }));
                  devices.push(...siteDevices);

                  const sortedItems = [...item.Devices.items].sort((a, b) => {
                    const aIsReference = a.DeviceType?.displayName === 'Reference';
                    const bIsReference = b.DeviceType?.displayName === 'Reference';
                    if (aIsReference && !bIsReference) return 1;
                    if (!aIsReference && bIsReference) return -1;
                    return a.displayName.localeCompare(b.displayName);
                  });

                  const newSite = { ...item, Devices: sortedItems, checked: true, country: item.country };
                  sites.push(newSite);
                  sites.sort((a, b) => a?.country?.localeCompare(b?.country));

                  devices.sort((a, b) => {
                    const aIsReference = a.DeviceType?.displayName === 'Reference';
                    const bIsReference = b.DeviceType?.displayName === 'Reference';
                    if (aIsReference && !bIsReference) return 1;
                    if (!aIsReference && bIsReference) return -1;
                    return a?.displayName.localeCompare(b?.displayName);
                  });

                  const countryData = groupedData.find(g => g._id === item._id_country);
                  return {
                    _id: item._id_country,
                    name: item.country,
                    checked: true,
                    sites: countryData?.sites || [],
                    regions:
                      countryData?.regions?.sort((a, b) =>
                        a.name.replace(/\s/g, '').localeCompare(b.name.replace(/\s/g, '')),
                      ) || [],
                  };
                });

                const uniqueCountries = countries.filter(
                  (item, index, self) => index === self.findIndex(i => i._id === item._id),
                ).sort((a, b) =>
                  a.name.replace(/\s/g, '').localeCompare(b.name.replace(/\s/g, '')),
                );

                const transformedPortfolio = {
                  _id: portfolio._id,
                  name: portfolio.displayName,
                  countriesNames: uniqueCountries.map(country => country.name),
                  devices,
                  countries: uniqueCountries,
                  sites,
                };

                return transformedPortfolio;
              });
            }),
            catchError(error => {
              this.portfolioListError.set(error);
              return of([]);
            }),
          ),
        ),
      ),
      { initialValue: null },
    );
  }

  getDeviceTypes() {
    return toSignal<any>(
      toObservable(this.updatePortfoliosList).pipe(
        switchMap(() =>
          this.deviceTypesGQL.fetch({}, { fetchPolicy: 'network-only' }).pipe(
            map(result => {
              return result.data.DeviceType.items.reduce(
                (acc, item) => {
                  acc[item._id] = item.name;
                  return acc;
                },
                {} as Record<number, string>,
              );
            }),
          ),
        ),
      ),
    );
  }
}
